/**
 * Alle Wände des Hauses als Daten.
 *
 * Eine Wand ist ein Rechteck im Grundriss (x0, y0, x1, y1) auf einem Geschoss f.
 * Öffnungen liegen entlang der Wand: a0/a1 sind absolute Planmaße (wie die Maßkette),
 * b0/b1 die Höhe über Fußboden. ext markiert Außenwände (Fassadenhaut auf der Seite),
 * roof lässt die Wandoberkante der Dachschräge folgen.
 *
 * Wand ändern oder Durchbruch einplanen = hier eine Zeile anpassen.
 */
import { GB, ST } from "./constants";
import type { Opening } from "./geometry";

export interface WallDef {
  f: number;
  x0: number; y0: number; x1: number; y1: number;
  ops?: Opening[];
  ext?: "N" | "S" | "E" | "W" | null;
  roof?: "top" | "under" | null;
  opt?: { h?: number; collide?: boolean };
}

/* Öffnungstypen */
export const win = (a0: number, a1: number, s = 0.84, h = 2.08): Opening => ({ a0, a1, b0: s, b1: h, k: "win" });
export const fix = (a0: number, a1: number): Opening => ({ a0, a1, b0: 0.04, b1: 2.1, k: "win" });
export const door = (a0: number, a1: number, h = 2.01): Opening => ({ a0, a1, b0: 0, b1: h, k: "door" });
export const gdoor = (a0: number, a1: number): Opening => ({ a0, a1, b0: 0, b1: 2.1, k: "gdoor" });
export const edoor = (a0: number, a1: number, h = 2.09): Opening => ({ a0, a1, b0: 0, b1: h, k: "edoor" });
export const block = (a0: number, a1: number, s: number, h: number): Opening => ({ a0, a1, b0: s, b1: h, k: "block" });
export const gate = (a0: number, a1: number, h: number): Opening => ({ a0, a1, b0: 0, b1: h, k: "gate" });
export const hole = (a0: number, a1: number, s: number, h: number): Opening => ({ a0, a1, b0: s, b1: h, k: "hole" });

export const W = (f: number, x0: number, y0: number, x1: number, y1: number, ops: Opening[] = [], ext: WallDef["ext"] = null, roof: WallDef["roof"] = null): WallDef =>
  ({ f, x0, y0, x1, y1, ops, ext, roof });

export const WALLS: WallDef[] = [
  /* ---------- UG ±0,00 ---------- */
  W(0, 0, 0, 7.75, 0.3, [win(1.21, 3.1, 0.86, 2.06), edoor(5.1, 6.18), block(6.18, 7.3, 0.1, 2.09)], "S"),
  W(0, 0, 10.4, 4.5, 11.0, [], "N"),
  W(0, 4.5, 10.7, 7.75, 11.0, [win(5.14, 7.14, 1.7, 2.3)], "N"),
  W(0, 0, 0.3, 0.3, 10.4, [door(4.14, 4.98), win(5.4, 6.3, 1.7, 2.3)], "W"),
  W(0, 7.45, 0.3, 7.75, 10.7, [], "E"),
  W(0, 0.3, 3.9, 4.26, 4.02),
  W(0, 4.26, 0.3, 4.5, 4.02, [door(2.05, 3.07)]),
  W(0, 4.5, 1.9, 7.45, 2.07, [door(5.1, 6.12)]),
  W(0, 0.3, 5.07, 4.26, 5.19, [door(1.99, 2.79)]),
  W(0, 4.26, 5.07, 4.5, 5.4),
  W(0, 0.3, 6.52, 2.76, 6.76, [hole(1.13, 1.93, 1.05, 1.65)]), // Öltankraum: nur Guckloch
  W(0, 2.76, 6.64, 4.26, 6.76),
  W(0, 2.76, 6.76, 3.0, 10.4),
  W(0, 4.5, 6.64, 5.82, 6.76, [door(4.92, 5.74)]),
  W(0, 5.82, 6.64, 7.45, 6.76),
  W(0, 5.82, 5.52, 7.45, 5.76),
  // Garage mit Außentreppe zum Garten
  W(0, -3.59, 0.79, -3.38, ST, [], "W"),
  W(0, -3.38, 1.63, 0, 1.87, [gate(-2.77, -0.55, 2.09)], "S"),
  W(0, -3.38, GB - 0.25, 0, GB, [door(-3.16, -2.26)]),
  W(0, -2.02, GB, -1.78, ST, [], "E"),

  /* ---------- EG +2,60 ---------- */
  W(1, 0, 0, 7.75, 0.3, [win(1.22, 3.11), win(3.32, 3.98), win(5.11, 6.13), win(6.29, 7.33)], "S"),
  W(1, 0, 10.4, 4.5, 10.7, [fix(0.98, 2.45), gdoor(2.45, 3.68)], "N"),
  W(1, 4.2, 10.7, 7.75, 11.0, [win(5.11, 7.03)], "N"),
  W(1, 0, 0.3, 0.3, 10.4, [gdoor(2.42, 3.44), win(5.0, 6.0)], "W"),
  W(1, 7.45, 0.3, 7.75, 10.7, [], "E"),
  W(1, 0.3, 3.9, 1.98, 4.02), W(1, 3.11, 3.9, 4.24, 4.02), // ohne Wandbacken links von der Flurwand (gibt es nicht)
  W(1, 0.3, 6.52, 2.14, 6.76), W(1, 3.76, 6.52, 4.24, 6.76),
  W(1, 3.11, 0.3, 3.26, 3.9, [door(2.2, 3.02)]),
  W(1, 3.26, 1.88, 4.24, 2.06, [door(3.32, 4.18)]),
  W(1, 4.24, 0.3, 4.48, 2.06),
  W(1, 4.48, 1.88, 5.14, 2.06),
  W(1, 6.13, 0.3, 6.28, 1.88, [door(0.84, 1.7)]),
  W(1, 6.13, 1.88, 7.45, 2.07),
  W(1, 4.24, 3.08, 4.48, 5.4),
  W(1, 4.48, 5.53, 7.45, 5.77, [door(4.96, 5.86)]),
  W(1, 4.24, 6.78, 4.48, 7.03), // Wandstück vor dem Schornstein in den Wohnraum

  /* ---------- OG +5,35 ---------- */
  W(2, 0, 0, 7.75, 0.3, [win(1.22, 3.11), win(5.1, 6.1), win(6.27, 7.28)], "S"),
  W(2, 0, 10.4, 4.5, 10.7, [fix(1.04, 2.48), gdoor(2.48, 3.71)], "N"),
  W(2, 4.2, 10.7, 7.75, 11.0, [win(5.17, 7.05)], "N"),
  W(2, 0, 0.3, 0.3, 10.4, [win(2.48, 3.5), win(4.99, 6.01)], "W"),
  W(2, 7.45, 0.3, 7.75, 10.7, [], "E"),
  W(2, 0.3, 3.9, 4.51, 4.02),
  W(2, 4.28, 0.3, 4.51, 3.9, [door(2.48, 3.32)]),
  W(2, 4.51, 1.9, 7.45, 2.07, [door(4.91, 5.68)]),
  W(2, 2.3, 4.02, 2.44, 6.52, [door(4.87, 5.71)]),
  W(2, 0.3, 6.52, 4.28, 6.64, [door(2.72, 3.5)]),
  W(2, 4.28, 6.73, 4.51, 10.7),
  W(2, 4.51, 5.53, 7.45, 5.77, [door(4.95, 5.85)]),

  /* ---------- DG +8,10 ---------- */
  W(3, 0.3, 0, 7.45, 0.3, [], "S", "under"),
  W(3, 0.3, 10.7, 7.45, 11.0, [], "N", "under"),
  W(3, 0, 0, 0.3, 11.0, [win(5.0, 6.02, 0.9, 2.0)], "W", "under"),
  W(3, 7.45, 0, 7.75, 11.0, [], "E", "under"),
  W(3, 4.26, 0.3, 4.5, 5.4, [door(4.31, 5.0, 2.0)], null, "under"),
  W(3, 4.26, 6.78, 4.5, 10.7, [], null, "under"),
  // Abseiten (Drempelwände) auf beiden Dachseiten, links und rechts; Luke nur zur Straße
  W(3, 4.5, 1.89, 7.45, 2.07, [door(5.13, 5.96, 0.95)], null, "under"),
  W(3, 0.3, 1.89, 4.26, 2.07, [door(1.87, 2.7, 0.95)], null, "under"),
  W(3, 0.3, 8.93, 4.26, 9.11, [], null, "under"),
  W(3, 4.5, 8.93, 7.45, 9.11, [], null, "under"),
  W(3, 4.5, 5.53, 7.45, 5.77, [door(4.95, 5.87, 2.0)], null, "under"),
];
